import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { questionsService } from '@/services/questions.service';
import {
  TrendingUp,
  DollarSign,
  CheckCircle,
  Zap,
  Database,
  Server,
  Brain,
  TrendingDown,
  ChevronRight,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

export function Analytics() {
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');

  const { data: analytics, isLoading } = useQuery({
    queryKey: ['analytics', timeRange],
    queryFn: async () => {
      try {
        return await questionsService.getAnalytics();
      } catch (error) {
        console.error('Failed to fetch analytics:', error);
        return null;
      }
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-gray-500">Loading analytics...</div>
      </div>
    );
  }

  const stats = analytics?.stats;
  const generationTrend = analytics?.generation_trend;
  const costTrend = analytics?.cost_trend;
  const llmCosts = analytics?.llm_costs;
  const ragMetrics = analytics?.rag_metrics;
  const cacheMetrics = analytics?.cache_metrics;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Analytics Overview</h1>
          <p className="text-sm text-gray-600 mt-1">Key performance metrics and system insights</p>
        </div>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value as typeof timeRange)}
          className="input-field w-48"
        >
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
          <option value="90d">Last 90 Days</option>
        </select>
      </div>

      {/* Analytics Navigation */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/analytics/costs"
            className="p-4 border-2 border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-all group"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-green-100 rounded-lg group-hover:bg-green-200">
                  <DollarSign className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">LLM Costs</h3>
                  <p className="text-sm text-gray-600">Detailed cost analysis</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-primary-600" />
            </div>
          </Link>

          <Link
            to="/analytics/rag"
            className="p-4 border-2 border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-all group"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-blue-100 rounded-lg group-hover:bg-blue-200">
                  <Database className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">RAG Performance</h3>
                  <p className="text-sm text-gray-600">Retrieval metrics</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-primary-600" />
            </div>
          </Link>

          <Link
            to="/analytics/cache"
            className="p-4 border-2 border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-all group"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-orange-100 rounded-lg group-hover:bg-orange-200">
                  <Server className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Cache Analytics</h3>
                  <p className="text-sm text-gray-600">Cache performance</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-primary-600" />
            </div>
          </Link>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Questions Generated</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {stats?.total_questions.toLocaleString()}
              </p>
              <p className="text-sm text-green-600 mt-1">
                +{stats?.questions_growth_percent}% from last period
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Cost</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                ${stats?.total_cost.toFixed(2)}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                ${stats?.avg_cost_per_question.toFixed(4)}/question
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Quality Score</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {stats?.avg_quality_score}/100
              </p>
              <p className="text-sm text-green-600 mt-1">
                {stats?.high_quality_percent}% above 85
              </p>
            </div>
            <div className="p-3 bg-fk-orange-vibrant100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-#ea7500" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Cache Hit Rate</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {stats?.cache_hit_rate_percent}%
              </p>
              <p className="text-sm text-gray-600 mt-1">
                {stats?.avg_generation_time_sec.toFixed(1)}s avg time
              </p>
            </div>
            <div className="p-3 bg-orange-100 rounded-lg">
              <Zap className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Cost Savings Banner */}
      {llmCosts && (
        <div className="card bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
          <div className="flex items-start space-x-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <TrendingDown className="w-8 h-8 text-green-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-green-900 mb-2">Cost Optimization Impact</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-green-700">Cache Savings</p>
                  <p className="text-2xl font-bold text-green-900">${llmCosts.cost_savings.cache_savings.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-sm text-green-700">RAG Optimization</p>
                  <p className="text-2xl font-bold text-green-900">${llmCosts.cost_savings.rag_optimization_savings.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-sm text-green-700">Total Savings</p>
                  <p className="text-2xl font-bold text-green-900">${llmCosts.cost_savings.total_savings.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-sm text-green-700">Savings Rate</p>
                  <p className="text-2xl font-bold text-green-900">
                    {((llmCosts.cost_savings.total_savings / llmCosts.cost_savings.potential_cost_without_optimization) * 100).toFixed(1)}%
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* LLM Summary */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">LLM Usage</h3>
            <Brain className="w-5 h-5 text-blue-600" />
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Total Requests</span>
              <span className="font-semibold text-gray-900">
                {llmCosts?.by_model.reduce((sum: number, m: any) => sum + m.requests, 0).toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Total Tokens</span>
              <span className="font-semibold text-gray-900">
                {stats?.total_tokens.toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Avg Tokens/Question</span>
              <span className="font-semibold text-gray-900">
                {stats?.avg_tokens_per_question.toLocaleString()}
              </span>
            </div>
          </div>
          <Link to="/analytics/costs" className="mt-4 text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center">
            View detailed costs <ChevronRight className="w-4 h-4 ml-1" />
          </Link>
        </div>

        {/* RAG Summary */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">RAG Performance</h3>
            <Database className="w-5 h-5 text-#1e73be" />
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Avg Relevance</span>
              <span className="font-semibold text-gray-900">
                {(ragMetrics?.retrieval_stats?.avg_relevance_score ?? 0 * 100).toFixed(1)}%
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Quality Improvement</span>
              <span className="font-semibold text-green-600">
                +{ragMetrics?.effectiveness.rag_quality_improvement}%
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Chunks Used</span>
              <span className="font-semibold text-gray-900">
                {ragMetrics?.chunk_usage.chunks_used_in_generation.toLocaleString()}
              </span>
            </div>
          </div>
          <Link to="/analytics/rag" className="mt-4 text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center">
            View RAG metrics <ChevronRight className="w-4 h-4 ml-1" />
          </Link>
        </div>

        {/* Cache Summary */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Cache Stats</h3>
            <Server className="w-5 h-5 text-orange-600" />
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Hit Rate</span>
              <span className="font-semibold text-green-600">
                {cacheMetrics?.overall_stats.hit_rate.toFixed(1)}%
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Time Saved</span>
              <span className="font-semibold text-gray-900">
                {cacheMetrics?.performance.time_saved_hours.toFixed(1)}h
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Cost Savings</span>
              <span className="font-semibold text-green-600">
                ${cacheMetrics?.cost_impact.savings.toFixed(2)}
              </span>
            </div>
          </div>
          <Link to="/analytics/cache" className="mt-4 text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center">
            View cache details <ChevronRight className="w-4 h-4 ml-1" />
          </Link>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Generation Trend */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Question Generation Trend</h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={generationTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis />
                <Tooltip
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                  formatter={(value: number) => [value, 'Questions']}
                />
                <Bar dataKey="count" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Cost Trend */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Daily Cost Trend</h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={costTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis tickFormatter={(value) => `$${value}`} />
                <Tooltip
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                  formatter={(value: number) => [`$${value.toFixed(2)}`, 'Cost']}
                />
                <Line type="monotone" dataKey="cost" stroke="#10b981" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Token Usage Summary */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Token Usage Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-600 mb-1">Total Input Tokens</p>
            <p className="text-2xl font-bold text-blue-900">
              {stats?.total_input_tokens.toLocaleString()}
            </p>
          </div>
          <div className="p-4 bg-green-50 rounded-lg">
            <p className="text-sm text-green-600 mb-1">Total Output Tokens</p>
            <p className="text-2xl font-bold text-green-900">
              {stats?.total_output_tokens.toLocaleString()}
            </p>
          </div>
          <div className="p-4 bg-fk-orange-vibrant50 rounded-lg">
            <p className="text-sm text-#ea7500 mb-1">Total Tokens</p>
            <p className="text-2xl font-bold text-fk-orange-vibrant900">
              {stats?.total_tokens.toLocaleString()}
            </p>
          </div>
          <div className="p-4 bg-orange-50 rounded-lg">
            <p className="text-sm text-orange-600 mb-1">Avg Tokens/Question</p>
            <p className="text-2xl font-bold text-orange-900">
              {stats?.avg_tokens_per_question.toLocaleString()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
